import type { ReactNode } from 'react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, Loader2, Check, AlertCircle } from 'lucide-react'
import { useAppStore } from '@/store'
import { getCurrentDirection } from '@/i18n/config'
import { useTranslation } from '@/i18n'
import clsx from 'clsx'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import EditableTitle from '../EditableTitle'

type BoardLayoutAction = {
  onClick: () => void
  element: ReactNode
}

type BoardLayoutProps = {
  name: string
  children: ReactNode
  backUrl: string
  actions?: BoardLayoutAction[]
  handleBackCallback?: () => void
  tabs?: () => JSX.Element | undefined
  hasUnsavedChanges?: boolean
  boardId?: string
  onRename?: (newName: string) => void
  /** Optional custom save handler. If provided, will be called instead of saveBoard when navigating back with unsaved changes. */
  onSave?: () => Promise<boolean | void>
}

// Removed hardcoded strings - now using translations

const BoardLayout = ({
  name,
  children,
  backUrl,
  actions,
  handleBackCallback,
  tabs,
  hasUnsavedChanges,
  boardId,
  onRename,
  onSave,
}: BoardLayoutProps) => {
  const navigate = useNavigate()
  const boardHasPendingChanges = useAppStore((state) => state.hasPendingChanges)
  const boardIsSaving = useAppStore((state) => state.isSaving)
  // showSavingIndicator controls the "Auto saving..." UI (only true for user-initiated saves)
  const showSavingIndicator = useAppStore((state) => state.showSavingIndicator)
  const renameBoard = useAppStore((state) => state.renameBoard)
  const saveBoard = useAppStore((state) => state.saveBoard)
  // Check if saved indicator should be shown (save completed successfully)
  const showSavedIndicator = useAppStore((state) => state.showSavedIndicator)
  const { t } = useTranslation()

  // Show "Auto saving..." for at least 1 second so users can see it
  const [showAutoSaving, setShowAutoSaving] = useState(false)
  const autoSavingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Save-failed dialog and persistent error indicator
  const [showSaveFailedDialog, setShowSaveFailedDialog] = useState(false)
  const [showSaveError, setShowSaveError] = useState(false)

  // Prevent double-clicks on back button during save-on-exit
  const isSavingOnExitRef = useRef(false)

  useEffect(() => {
    if (showSavingIndicator) {
      // Start showing "Auto saving..." immediately (only for user-initiated saves)
      setShowAutoSaving(true)
      // Clear any pending hide timeout
      if (autoSavingTimeoutRef.current) {
        clearTimeout(autoSavingTimeoutRef.current)
        autoSavingTimeoutRef.current = null
      }
    } else if (showAutoSaving) {
      // Save finished - keep showing for at least 1 second total
      autoSavingTimeoutRef.current = setTimeout(() => {
        setShowAutoSaving(false)
        autoSavingTimeoutRef.current = null
      }, 1000)
    }

    return () => {
      if (autoSavingTimeoutRef.current) {
        clearTimeout(autoSavingTimeoutRef.current)
      }
    }
  }, [showSavingIndicator, showAutoSaving])

  // Clear save error indicator when a new save starts
  useEffect(() => {
    if (showSavingIndicator) {
      setShowSaveError(false)
    }
  }, [showSavingIndicator])

  // Check for unsaved changes from the prop or board state
  // Note: boardIsSaving is NOT included here - we disable the back button during save instead
  // Note: Graph changes are passed via hasUnsavedChanges prop and saved via onSave callback
  const hasChanges = !!hasUnsavedChanges || boardHasPendingChanges

  const latestHasUnsavedChangesRef = useRef(!!hasUnsavedChanges)

  useEffect(() => {
    latestHasUnsavedChangesRef.current = !!hasUnsavedChanges
  }, [hasUnsavedChanges])

  const getLatestHasChanges = useCallback(() => {
    // When not in a board context, only check the prop-based unsaved changes.
    // The board store's hasPendingChanges is irrelevant for non-board views
    // (graph editor, document viewer) and can be stale from a previously visited board.
    if (!boardId) {
      return latestHasUnsavedChangesRef.current
    }
    const state = useAppStore.getState()
    // Note: isSaving is NOT included - we disable back button during save instead
    // Note: Graph changes are tracked via hasUnsavedChanges prop (latestHasUnsavedChangesRef)
    // and saved via the onSave callback, not through board saves
    return (
      latestHasUnsavedChangesRef.current ||
      state.hasPendingChanges
    )
  }, [boardId])

  const isRTL = getCurrentDirection() === 'rtl'

  const handleRename = useCallback(
    (newName: string) => {
      if (boardId && newName !== name) {
        renameBoard(boardId, newName)
        onRename?.(newName)
      }
    },
    [boardId, name, renameBoard, onRename]
  )

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasChanges])

  const performNavigation = useCallback(() => {
    if (handleBackCallback && !backUrl) {
      // If no backUrl provided, use callback for custom navigation
      handleBackCallback()
    } else {
      // Otherwise use the provided URL
      navigate(backUrl)
      if (handleBackCallback) {
        handleBackCallback()
      }
    }
  }, [backUrl, handleBackCallback, navigate])

  const handleRetrySave = async () => {
    setShowSaveFailedDialog(false)
    try {
      if (onSave) {
        const result = await onSave()
        if (result === false) {
          setShowSaveError(true)
          return
        }
      } else {
        const saveSucceeded = await saveBoard(false)
        if (!saveSucceeded) {
          setShowSaveError(true)
          return
        }
      }
      setShowSaveError(false)
    } catch {
      setShowSaveError(true)
    }
  }

  const handleBack = async () => {
    // Don't allow navigation while already saving on exit (prevents double-clicks)
    if (isSavingOnExitRef.current) {
      return
    }
    // Only block on boardIsSaving when in a board context (boardId is set).
    // Non-board views (graph editor, document viewer) reuse BoardLayout but the board store's
    // isSaving may be stale from a previously visited board and should not block navigation.
    if (boardId && boardIsSaving) {
      return
    }

    if (getLatestHasChanges()) {
      // Prevent double-clicks
      isSavingOnExitRef.current = true

      try {
        // Auto-save before leaving instead of showing warning modal
        if (onSave) {
          // Use custom save handler (e.g., for graph editor)
          const result = await onSave()
          // If onSave returns false explicitly, show save-failed dialog
          if (result === false) {
            setShowSaveFailedDialog(true)
            return
          }
        } else {
          // Default: use board save
          // saveBoard shows its own error toast if it fails
          const saveSucceeded = await saveBoard(false)
          if (!saveSucceeded) {
            // Show save-failed dialog instead of silently returning
            setShowSaveFailedDialog(true)
            return
          }
        }
      } finally {
        isSavingOnExitRef.current = false
      }
    }

    performNavigation()
  }

  return (
      <div className="flex flex-col h-[100vh]">
      <header className={`flex items-center p-4 border-b border-gray-200 dark:border-dark-border justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* Left side (or right side in RTL) - Back button and title */}
        <div className={clsx(
          "flex items-center gap-3",
          isRTL ? "order-3" : "order-1"
        )}>
          <ArrowLeftIcon
            aria-label={t('common:buttons.back')}
            className={clsx(
              "w-6 h-6 text-orange-700 hover:text-orange-500 cursor-pointer transition-all",
              isRTL && "rotate-180",
              boardId && boardIsSaving && "opacity-50 pointer-events-none"
            )}
            onClick={handleBack}
          />
          {boardId ? (
            <EditableTitle
              title={name}
              onSave={handleRename}
              isRTL={isRTL}
            />
          ) : (
            <div className={clsx("flex items-center gap-2", isRTL && "flex-row-reverse")}>
              <h3
                className={clsx(
                  "text-lg font-medium truncate max-w-[40vw] cursor-pointer hover:text-orange-500 transition-colors",
                  isRTL ? "text-right" : "text-left"
                )}
                onClick={handleBack}
              >
                {name}
              </h3>
            </div>
          )}
          {/* Auto-save status indicator */}
          {showAutoSaving && (
            <div className={clsx("flex items-center gap-1.5 text-gray-500 dark:text-dark-text-muted", isRTL && "flex-row-reverse")}>
              <span className="text-sm">{t('common:buttons.auto_saving')}</span>
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          )}
          {showSavedIndicator && !showAutoSaving && (
            <div className={clsx("flex items-center gap-1.5 text-green-600", isRTL && "flex-row-reverse")}>
              <Check className="w-4 h-4" />
              <span className="text-sm">{t('common:buttons.saved')}</span>
            </div>
          )}
          {showSaveError && !showAutoSaving && (
            <div className={clsx("flex items-center gap-1.5 text-red-600", isRTL && "flex-row-reverse")}>
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{t('common:board.save_failed')}</span>
            </div>
          )}
        </div>

        {/* Center - Tabs */}
        <div className={clsx(isRTL ? "order-2" : "order-2")}>
          {tabs?.()}
        </div>

        {/* Right side (or left side in RTL) - Action buttons */}
        <div className={clsx(
          "flex items-center gap-3",
          isRTL ? "order-1" : "order-3"
        )}>
          {actions?.map(({ element, onClick }, index) => (
            <div key={index} onClick={onClick} className="cursor-pointer">
              {element}
            </div>
          ))}
        </div>
      </header>
      <div className="w-full flex-1 min-h-0 flex overflow-hidden">{children}</div>

      <AlertDialog
        open={showSaveFailedDialog}
        onOpenChange={(open) => {
          if (!open) {
            handleRetrySave()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common:modals.unsaved_changes.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common:modals.save_failed.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={handleRetrySave}
            >
              {t('common:modals.save_failed.stay')}
            </Button>
            <Button
              onClick={() => {
                setShowSaveFailedDialog(false)
                setShowSaveError(false)
                performNavigation()
              }}
            >
              {t('common:modals.save_failed.leave_without_saving')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default BoardLayout
